import { Injectable, inject } from '@angular/core';
import { Observable, share, tap } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { UserDataService } from './user-data.service';
import { LoggingService } from './logging.service';
import { ISharePlantPayload } from '../../shared/interfaces/ISharePlant';
import { ISharePlantFeedbackPayload } from '../../shared/interfaces/ISharePlantFeedback';

@Injectable({
  providedIn: 'root',
})
export class MapSocketService {
  user = inject(UserDataService);
  logging = inject(LoggingService);

  private socketStream: Socket;
  private mapSpace: Socket;

  public onRequestSharePlant$: Observable<ISharePlantPayload>;
  public onShareReportFeedback$: Observable<ISharePlantFeedbackPayload>;

  public socketDownValue = false;
  public reconnectAttempts = 0;
  public maxReconnectAttempts = 10;
  private socketOptions = {
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 7000,
    // Callback para ler o token atual a cada tentativa (o valor estático congelava o token da 1ª conexão)
    auth: (cb: (data: { token: string | null }) => void) =>
      cb({ token: this.user.getSocketCentralToken() }),
    transports: ['websocket'],
  };

  // CONNECTION map_space
  joinIdPlantMapRoom() {
    // para evitar multiplas instancias do socket: reusa sempre a mesma, reconectando se preciso
    if (this.mapSpace) {
      if (this.mapSpace.connected) {
        this.logging.logInfo(`${this.mapSpace.id}: Socket já conectado, reutilizando conexão.`);
      } else {
        this.reconnectAttempts = 0;
        this.mapSpace.connect();
      }
      return;
    }

    this.reconnectAttempts = 0;
    this.mapSpace = io(environment.socketCentralUrl + '/map_space', this.socketOptions);

    this.mapSpace.on('send_room', () => {
      this.reconnectAttempts = 0;
      this.mapSpace.emit('join', this.user.getIdPlanta());
      this.logging.logInfo(`${this.mapSpace.id}: Usuário conectado ao socket central.`);
    });

    if (!this.onRequestSharePlant$) {
      this.onRequestSharePlant$ = new Observable<ISharePlantPayload>((observer) => {
        this.mapSpace.on('share_planta_request', (data) => observer.next(data));
      }).pipe(
        tap(() => this.logging.logInfo(`${this.mapSpace.id}: Recebeu 'share_planta_request'.`)),
        share()
      );
    }

    if (!this.onShareReportFeedback$) {
      this.onShareReportFeedback$ = new Observable<ISharePlantFeedbackPayload>((observer) => {
        this.mapSpace.on('share_planta_feedback', (data) => observer.next(data));
      }).pipe(
        tap(() => this.logging.logInfo(`${this.mapSpace.id}: Recebeu 'share_planta_feedback'.`)),
        share()
      );
    }
  }

  // HANDLERS socketSat
  onNewSatData() {
    return new Observable<boolean>((observer) => {
      const handler = (msg: boolean) => {
        this.logging.logInfo(
          `${this.mapSpace.id}: Solicitação de atualização de dados satelital recebida.`
        );
        observer.next(msg);
      };
      this.mapSpace.on('sat_update', handler);
      // Remove só este handler: o socket é compartilhado com outros consumidores.
      return () => {
        this.mapSpace.off('sat_update', handler);
      };
    });
  }

  // HANDLERS socketBrigade
  onNewBrigadetData() {
    return new Observable<boolean>((observer) => {
      const handler = (msg: boolean) => {
        this.logging.logInfo(
          `WebSocket: Solicitação de atualização de dados de brigadas recebida.`
        );
        observer.next(msg);
      };
      this.mapSpace.on('brigade_update', handler);
      // Remove só este handler: o socket é compartilhado com outros consumidores.
      return () => {
        this.mapSpace.off('brigade_update', handler);
      };
    });
  }

  // HANDLERS compartilhamento de relatório (map-p3). Os campos `onRequestSharePlant$` e
  // `onShareReportFeedback$` acima são do /map e não removem o handler do socket.
  onSharePlantRequest() {
    return this.listen<ISharePlantPayload>('share_planta_request');
  }

  onSharePlantFeedback() {
    return this.listen<ISharePlantFeedbackPayload>('share_planta_feedback');
  }

  private listen<T>(event: string): Observable<T> {
    return new Observable<T>((observer) => {
      const handler = (msg: T) => {
        this.logging.logInfo(`${this.mapSpace.id}: Recebeu '${event}'.`);
        observer.next(msg);
      };
      this.mapSpace.on(event, handler);
      // Remove só este handler: o socket é compartilhado com outros consumidores.
      return () => {
        this.mapSpace.off(event, handler);
      };
    });
  }

  onAttDadosEvent() {
    return new Observable<boolean>((observer) => {
      this.mapSpace.on('trigger_att_dados_planta', (msg) => {
        this.logging.logInfo(
          `${this.mapSpace.id}: Recebeu solicitação de atualização de dados da planta.`
        );
        this.logging.logInfo('Recebeu solicitação de atualização de dados da planta');
        observer.next(msg);
        this.sendFeedBackAtualizarDadosRemoto();
      });
    });
  }

  //Retorno do cliente ao receber o trigger de att dados remotos
  onFeedBackAttDadosEvent() {
    return new Observable<{ success: boolean; numClients: number }>((observer) => {
      this.mapSpace.on('trigger_feedback_atualizar_dados_planta', (msg) => {
        this.logging.logInfo(
          `Dados atualizados no lado do cliente. Número de clientes: ${msg.numClientes}`
        );
        observer.next(msg);
      });
    });
  }

  // Busca total de peers conectados (admin)
  getTotalPeers(): Observable<{
    totalPeers: number;
    plants: { id_planta: number; peers: number }[];
  }> {
    return new Observable((observer) => {
      this.mapSpace.emit('get_total_peers');
      this.mapSpace.once('total_peers_response', (data) => {
        observer.next(data);
      });
    });
  }

  onSocketCentralDisconnect() {
    return new Observable<string>((observer) => {
      this.mapSpace.on('disconnect', (msg) => {
        observer.next(msg);
      });
    });
  }

  onSocketCentralDown() {
    return new Observable<string>((observer) => {
      this.mapSpace.on('connect_error', (error: any) => {
        this.reconnectAttempts++;
        if (this.reconnectAttempts === this.maxReconnectAttempts) {
          this.logging.logError(`socket:onSatSocketDown`, error);
          observer.next('true');
        }
      });
    });
  }

  // EMITERS socketSat
  sendAttSatConfirmation() {
    this.logging.logInfo(`${this.mapSpace.id}: Atualização realizada: dados da detecção satelital`);
  }

  // Emite atualização de dados via socket central
  sendAtualizarDadosRemoto() {
    this.mapSpace.emit('atualizar_dados_planta', this.user.getIdPlanta());
    this.logging.logInfo(`${this.mapSpace.id}: Atualizados dados do cliente.`);
  }

  //Retorno do cliente ao receber o trigger de att dados remotos
  sendFeedBackAtualizarDadosRemoto() {
    this.mapSpace.emit('feedback_atualizar_dados_planta', this.user.getIdPlanta());
  }

  disconnectFromSockCent() {
    try {
      this.logging.logInfo(`${this.mapSpace.id}: Usuário se desconectou do socket central.`);
      this.mapSpace.disconnect();
    } catch (error) {
      this.logging.logError(`socket:disconnectFromSockCent`, error);
    }
  }
}
